/**
 * Lot 3.2 — CPF (administrateur) : certifications de l'organisme et barème Mon Compte Formation.
 * Décision du 30/10/2026 : pas de circuit de financement CPF dans le SaaS ; la demande part par e-mail vers Formatrix.
 * Import du barème en deux temps : aperçu (lignes, erreurs, avertissements), puis remplacement confirmé.
 * Aucun montant réglementaire n'est affiché ni calculé : Mon Compte Formation le donne à l'apprenant.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Archive, Check, Download, ExternalLink, GraduationCap, Plus, Send } from "lucide-react";
import { EXEMPLE_TARIFS_CPF_CSV } from "@/domaine/cpf/tarifs";
import { api, dateFr, ErreurApi, euros, heuresFr, instantFr, type ApercuTarifs, type Certification, type Dossier, type TarifCpf } from "../api";
import { useActeur } from "../session";
import { useRafraichirDossier } from "./DossierPieces";
import { Alerte, Bouton, Carte, Champ, Chargement, DepotFichier, EtatVide, Etiquette, Modale, Onglets, Selecteur, TitrePage, useNotifier, ZoneTexte } from "../ui/base";

export function AdminCpf() {
  const [onglet, setOnglet] = useState<"bareme" | "certifications">("bareme");
  return (
    <>
      <TitrePage titre="CPF" soustitre="Le barème de vos offres sur Mon Compte Formation et vos certifications, repris dans la demande CPF que le formateur transmet à Formatrix depuis un dossier." />
      <Onglets actif={onglet} choisir={setOnglet} onglets={[{ cle: "bareme", libelle: "Barème Mon Compte Formation" }, { cle: "certifications", libelle: "Certifications" }]} />
      {onglet === "bareme" ? <Bareme /> : <Certifications />}
    </>
  );
}

function Bareme() {
  const requetes = useQueryClient();
  const notifier = useNotifier();
  const tarifs = useQuery({ queryKey: ["cpf-tarifs"], queryFn: () => api.get<TarifCpf[]>("/cpf/tarifs") });
  const [apercu, setApercu] = useState<ApercuTarifs | null>(null);
  const lire = useMutation({ mutationFn: (f: File) => api.fichier<ApercuTarifs>("/admin/cpf/tarifs/apercu", f), onSuccess: setApercu });
  const remplacer = useMutation({
    mutationFn: () => api.put<{ importees: number }>("/admin/cpf/tarifs", { lignes: apercu!.lignes }),
    onSuccess: async (r) => {
      setApercu(null);
      await requetes.invalidateQueries({ queryKey: ["cpf-tarifs"] });
      notifier("succes", `Barème remplacé : ${r.importees} ligne(s).`);
    },
  });
  const exemple = `data:text/csv;charset=utf-8,${encodeURIComponent(`\uFEFF${EXEMPLE_TARIFS_CPF_CSV}`)}`;

  return (
    <div className="space-y-5">
      <Carte className="p-5">
        <h2 className="text-base font-semibold">Importer le barème</h2>
        <p className="mt-0.5 text-[13px] text-encre-2">Fichier CSV ou Excel (.xlsx), une ligne par offre : catégorie, intitulé, durée en heures, prix affiché, lien de l'offre sur moncompteformation.gouv.fr. Vous vérifiez l'aperçu avant de remplacer le barème actuel.</p>
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <DepotFichier libelle="Choisir un fichier" accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" enCours={lire.isPending} deposer={(f) => lire.mutate(f)} />
          <a href={exemple} download="exemple-bareme-cpf.csv" className="inline-flex items-center gap-1.5 text-sm font-medium text-accent hover:underline">
            <Download className="size-4" aria-hidden /> Fichier d'exemple
          </a>
        </div>
        {lire.error && <div className="mt-3"><Alerte ton="danger">{lire.error.message}</Alerte></div>}
      </Carte>

      {tarifs.isPending ? (
        <Chargement />
      ) : tarifs.error ? (
        <Alerte ton="danger">{tarifs.error.message}</Alerte>
      ) : tarifs.data.length === 0 ? (
        <EtatVide icone={<GraduationCap className="size-8" aria-hidden />} titre="Aucun barème importé">Importez le fichier de vos offres pour que les formateurs puissent envoyer le lien Mon Compte Formation à leurs apprenants.</EtatVide>
      ) : (
        <TableauTarifs lignes={tarifs.data} titre={`Barème actuel — importé le ${instantFr(tarifs.data[0]!.importe_le)}`} />
      )}

      <Modale ouverte={apercu !== null} fermer={() => setApercu(null)} titre={`Aperçu de « ${apercu?.nom_fichier ?? ""} »`} large>
        {apercu && (
          <div className="space-y-4">
            <p className="text-sm">
              <strong>{apercu.lignes.length}</strong> ligne(s) prête(s) à importer{apercu.erreurs.length > 0 && <>, <strong className="text-danger-encre">{apercu.erreurs.length}</strong> écartée(s)</>}.
            </p>
            {apercu.erreurs.length > 0 && <Alerte ton="danger" titre="Lignes écartées"><ul className="list-disc pl-4">{apercu.erreurs.slice(0, 20).map((x) => <li key={x}>{x}</li>)}</ul></Alerte>}
            {apercu.avertissements.length > 0 && <Alerte ton="attention" titre="À vérifier"><ul className="list-disc pl-4">{apercu.avertissements.slice(0, 20).map((x) => <li key={x}>{x}</li>)}</ul></Alerte>}
            {apercu.lignes.length > 0 && <TableauTarifs lignes={apercu.lignes.slice(0, 50)} titre={apercu.lignes.length > 50 ? "Les 50 premières lignes" : "Lignes lues"} />}
            {remplacer.error && <Alerte ton="danger">{remplacer.error.message}</Alerte>}
            <div className="flex flex-wrap justify-end gap-2">
              <Bouton variante="discret" onClick={() => setApercu(null)}>Annuler</Bouton>
              <Bouton variante="primaire" disabled={apercu.lignes.length === 0} enCours={remplacer.isPending} onClick={() => remplacer.mutate()}>
                Remplacer le barème ({apercu.lignes.length} ligne{apercu.lignes.length > 1 ? "s" : ""})
              </Bouton>
            </div>
          </div>
        )}
      </Modale>
    </div>
  );
}

function TableauTarifs({ lignes, titre }: { lignes: Array<Pick<TarifCpf, "categorie" | "intitule" | "duree_heures" | "prix_centimes" | "url_moncompteformation">>; titre: string }) {
  return (
    <section>
      <h2 className="mb-2 text-base font-semibold">{titre}</h2>
      {/* « relative » : les textes réservés aux lecteurs d'écran (sr-only, en position absolue) restent dans la zone défilante. */}
      <Carte className="relative overflow-x-auto" tabIndex={0} role="region" aria-label={titre}>
        <table className="w-full min-w-[560px] text-sm">
          <thead>
            <tr className="border-b border-trait text-left text-xs tracking-wide text-encre-3 uppercase">
              <th className="px-4 py-2.5 font-semibold">Offre</th>
              <th className="px-4 py-2.5 text-right font-semibold">Durée</th>
              <th className="px-4 py-2.5 text-right font-semibold">Prix affiché</th>
              <th className="px-4 py-2.5 font-semibold">Lien</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-trait">
            {lignes.map((l, i) => (
              <tr key={`${l.intitule}-${l.duree_heures}-${i}`}>
                <td className="px-4 py-2.5"><span className="font-medium">{l.intitule}</span><span className="block text-[12.5px] text-encre-3">{l.categorie}</span></td>
                <td className="chiffres px-4 py-2.5 text-right text-encre-2">{l.duree_heures} h</td>
                <td className="chiffres px-4 py-2.5 text-right text-encre-2">{euros(l.prix_centimes)}</td>
                <td className="px-4 py-2.5">{l.url_moncompteformation ? <a href={l.url_moncompteformation} target="_blank" rel="noreferrer noopener" className="inline-flex items-center gap-1 text-[13px] text-accent hover:underline">Ouvrir <ExternalLink className="size-3.5" aria-hidden /><span className="sr-only"> (nouvel onglet)</span></a> : <span className="text-[13px] text-encre-3">aucun</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Carte>
    </section>
  );
}

function Certifications() {
  const requetes = useQueryClient();
  const notifier = useNotifier();
  const liste = useQuery({ queryKey: ["cpf-certifications"], queryFn: () => api.get<Certification[]>("/cpf/certifications") });
  const [nouvelle, setNouvelle] = useState(false);
  const archiver = useMutation({
    mutationFn: (id: string) => api.post(`/admin/cpf/certifications/${id}/archiver`, { archiver: true }),
    onSuccess: async () => {
      await requetes.invalidateQueries({ queryKey: ["cpf-certifications"] });
      notifier("succes", "Certification archivée.");
    },
  });
  if (liste.isPending) return <Chargement />;
  if (liste.error) return <Alerte ton="danger">{liste.error.message}</Alerte>;
  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Bouton variante="signal" icone={<Plus className="size-4" aria-hidden />} onClick={() => setNouvelle(true)}>Nouvelle certification</Bouton>
      </div>
      {liste.data.length === 0 ? (
        <EtatVide icone={<GraduationCap className="size-8" aria-hidden />} titre="Aucune certification">Enregistrez les certifications que vous préparez (code RS ou RNCP de France Compétences) : leur fiche officielle accompagne le lien envoyé à l'apprenant.</EtatVide>
      ) : (
        <ul className="space-y-2">
          {liste.data.map((c) => (
            <li key={c.id}>
              <Carte className="flex flex-wrap items-center justify-between gap-3 p-4">
                <div className="min-w-0">
                  <p className="flex flex-wrap items-center gap-2">
                    {c.code && <Etiquette ton="accent">{c.code}</Etiquette>}
                    <span className="font-medium">{c.intitule}</span>
                    {!c.eligible_cpf && <Etiquette>Non éligible CPF</Etiquette>}
                  </p>
                  <p className="mt-0.5 text-[13px] text-encre-2">
                    {c.certificateur || "Certificateur non précisé"}
                    {c.fiche && <> · <a href={c.fiche} target="_blank" rel="noreferrer noopener" className="text-accent hover:underline">fiche France Compétences<span className="sr-only"> (nouvel onglet)</span></a></>}
                  </p>
                </div>
                <Bouton taille="sm" variante="discret" icone={<Archive className="size-3.5" aria-hidden />} onClick={() => archiver.mutate(c.id)}>Archiver</Bouton>
              </Carte>
            </li>
          ))}
        </ul>
      )}
      {nouvelle && <NouvelleCertification fermer={() => setNouvelle(false)} />}
    </div>
  );
}

function NouvelleCertification({ fermer }: { fermer: () => void }) {
  const requetes = useQueryClient();
  const [v, setV] = useState({ code: "", intitule: "", certificateur: "", eligible_cpf: true });
  const enregistrer = useMutation({
    mutationFn: () => api.post("/admin/cpf/certifications", v),
    onSuccess: async () => {
      await requetes.invalidateQueries({ queryKey: ["cpf-certifications"] });
      fermer();
    },
  });
  const err = enregistrer.error instanceof ErreurApi ? (enregistrer.error.details?.champs ?? {}) : {};
  return (
    <Modale ouverte fermer={fermer} titre="Nouvelle certification">
      <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); enregistrer.mutate(); }}>
        <Champ libelle="Code France Compétences" placeholder="RS6962 ou RNCP35614" aide="Facultatif : laissez vide pour une certification sans fiche." erreur={err.code} value={v.code} onChange={(e) => setV({ ...v, code: e.target.value })} />
        <Champ libelle="Intitulé" required erreur={err.intitule} value={v.intitule} onChange={(e) => setV({ ...v, intitule: e.target.value })} />
        <Champ libelle="Certificateur" erreur={err.certificateur} value={v.certificateur} onChange={(e) => setV({ ...v, certificateur: e.target.value })} />
        <label className="flex items-start gap-3 text-sm">
          <input type="checkbox" className="mt-0.5 size-4 accent-[var(--color-accent)]" checked={v.eligible_cpf} onChange={(e) => setV({ ...v, eligible_cpf: e.target.checked })} />
          <span>Éligible au CPF</span>
        </label>
        {enregistrer.error && Object.keys(err).length === 0 && <Alerte ton="danger">{enregistrer.error.message}</Alerte>}
        <div className="flex justify-end gap-2">
          <Bouton variante="discret" onClick={fermer}>Annuler</Bouton>
          <Bouton type="submit" variante="primaire" enCours={enregistrer.isPending}>Enregistrer</Bouton>
        </div>
      </form>
    </Modale>
  );
}

/** Depuis la fiche dossier : envoyer à un apprenant le lien de l'offre sur Mon Compte Formation. */
/**
 * Décision du 30/10/2026 — pas de circuit de financement CPF dans le SaaS : depuis la fiche dossier, le formateur (ou
 * l'organisme) transmet la demande par e-mail à Formatrix. Confirmation avant envoi, date de la dernière transmission
 * affichée, envoi journalisé. Sans adresse dans « Organisme », le bouton est désactivé et la raison est écrite.
 */
export function TransmissionCpf({ d }: { d: Dossier }) {
  const acteur = useActeur();
  const [ouverte, setOuverte] = useState(false);
  const etat = d.demande_cpf!;
  const sansAdresse = !etat.destinataire;
  return (
    <Carte className="p-5">
      <h2 className="flex items-center gap-2 text-base font-semibold">
        <GraduationCap className="size-4 text-encre-3" aria-hidden /> Demande CPF
      </h2>
      <p className="mt-0.5 text-[13px] text-encre-2">Le financement CPF est traité par Formatrix : la demande lui est transmise par e-mail avec le récapitulatif du dossier, et Formatrix inscrit l'apprenant sur Mon Compte Formation.</p>
      {etat.transmise_le ? (
        <p className="mt-3 flex items-start gap-2 text-sm">
          <Check className="mt-0.5 size-4 shrink-0 text-valide" strokeWidth={3} aria-hidden />
          <span>
            Demande transmise le <strong className="chiffres">{instantFr(etat.transmise_le)}</strong>
            {etat.destinataire && <span className="break-all text-encre-2"> à {etat.destinataire}</span>}.
          </span>
        </p>
      ) : (
        <p className="mt-3 text-sm text-encre-2">Aucune demande transmise pour ce dossier.</p>
      )}
      <div className="mt-3">
        <Bouton variante="secondaire" icone={<Send className="size-4" aria-hidden />} disabled={sansAdresse} aria-describedby={sansAdresse ? `cpf-sans-adresse-${d.id}` : undefined} onClick={() => setOuverte(true)}>
          Transmettre la demande CPF à Formatrix
        </Bouton>
      </div>
      {sansAdresse && (
        <div id={`cpf-sans-adresse-${d.id}`} className="mt-3">
          <Alerte ton="attention" titre="À renseigner dans Organisme">
            {acteur.role === "admin" ? (
              <>L'adresse de transmission des demandes CPF (Formatrix) est vide : <Link to="/admin/organisme" className="font-medium text-accent hover:underline">la saisir dans Organisme</Link>.</>
            ) : (
              <>L'organisme n'a pas encore saisi l'adresse de transmission des demandes CPF (Formatrix). Contactez-le.</>
            )}
          </Alerte>
        </div>
      )}
      {ouverte && <ConfirmationCpf d={d} destinataire={etat.destinataire} fermer={() => setOuverte(false)} />}
    </Carte>
  );
}

function ConfirmationCpf({ d, destinataire, fermer }: { d: Dossier; destinataire: string; fermer: () => void }) {
  const notifier = useNotifier();
  const rafraichir = useRafraichirDossier(d.id);
  const tarifs = useQuery({ queryKey: ["cpf-tarifs"], queryFn: () => api.get<TarifCpf[]>("/cpf/tarifs") });
  const certifications = useQuery({ queryKey: ["cpf-certifications"], queryFn: () => api.get<Certification[]>("/cpf/certifications") });
  const [cert, setCert] = useState("");
  const [tarif, setTarif] = useState("");
  const [commentaire, setCommentaire] = useState("");
  const envoyer = useMutation({
    mutationFn: () => api.post<{ destinataire: string }>(`/dossiers/${d.id}/demande-cpf`, { ...(cert ? { certification_id: cert } : {}), ...(tarif ? { tarif_id: tarif } : {}), commentaire }),
    onSuccess: async (r) => {
      await rafraichir();
      notifier("succes", `Demande CPF transmise à ${r.destinataire}.`);
      fermer();
    },
  });
  const f = d.formation;
  const recap: Array<[string, string]> = [
    ["Formation", f.formation_titre],
    ["Dates", f.formation_date_debut ? `${dateFr(f.formation_date_debut)}${f.formation_date_fin && f.formation_date_fin !== f.formation_date_debut ? ` → ${dateFr(f.formation_date_fin)}` : ""}` : "à définir"],
    ["Durée", heuresFr(f.formation_duree_heures_total)],
    [d.stagiaires.length > 1 ? "Apprenants" : "Apprenant", d.stagiaires.map((x) => `${x.prenom} ${x.nom}`).join(", ")],
    ["Formateur", `${d.formateur.prenom} ${d.formateur.nom}`],
    ["Montant HT", d.finances ? euros(d.finances.formation_prix_total_ht) : "—"],
  ];
  return (
    <Modale ouverte fermer={fermer} titre="Transmettre la demande CPF à Formatrix">
      <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); envoyer.mutate(); }}>
        <p className="text-sm text-encre-2">
          Un e-mail part à <strong className="break-all text-encre">{destinataire}</strong> avec ce récapitulatif. Vérifiez-le avant de confirmer.
        </p>
        <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-1 rounded-md border border-trait bg-papier-2 px-3 py-2.5 text-sm">
          {recap.map(([k, v]) => (
            <div key={k} className="contents">
              <dt className="text-encre-3">{k}</dt>
              <dd className="min-w-0 break-words">{v || "—"}</dd>
            </div>
          ))}
        </dl>
        <Selecteur libelle="Certification RS / RNCP (facultatif)" aide="Sa fiche France Compétences est jointe au message." value={cert} onChange={(e) => setCert(e.target.value)}>
          <option value="">— Aucune —</option>
          {(certifications.data ?? []).filter((c) => c.eligible_cpf).map((c) => <option key={c.id} value={c.id}>{c.code ? `${c.code} — ` : ""}{c.intitule}</option>)}
        </Selecteur>
        <Selecteur libelle="Offre du barème Mon Compte Formation (facultatif)" value={tarif} onChange={(e) => setTarif(e.target.value)}>
          <option value="">— Aucune —</option>
          {(tarifs.data ?? []).map((t) => <option key={t.id} value={t.id}>{t.intitule} · {t.duree_heures} h · {euros(t.prix_centimes)}</option>)}
        </Selecteur>
        <ZoneTexte libelle="Commentaire pour Formatrix (facultatif)" rows={2} maxLength={2000} value={commentaire} onChange={(e) => setCommentaire(e.target.value)} />
        {envoyer.error && <Alerte ton="danger">{envoyer.error.message}</Alerte>}
        <div className="flex flex-wrap justify-end gap-2">
          <Bouton variante="discret" onClick={fermer}>Annuler</Bouton>
          <Bouton type="submit" variante="primaire" enCours={envoyer.isPending}>Confirmer l'envoi</Bouton>
        </div>
      </form>
    </Modale>
  );
}
